import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Box, Divider, Paper, styled, Tab, Theme } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useParams, useNavigate } from 'react-router-dom';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useQuery } from '@apollo/client';
import { labClientTemplate } from 'apollo/templates/client-directory';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { permissions } from 'utils/permission';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CustomerType } from 'models/client-directory';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import ClientSettingsHeader from 'components/client-directory/client-settings/client-settings-header/client-settings-header.component';
import ClientDetails from 'components/client-directory/client-settings/client-details/client-details.component';
import ClientDirectoryDentists from 'components/client-directory/client-settings/dentists/client-directory-dentists.component';
import PriceList from 'components/client-directory/client-settings/price-list/price-list.component';
import TransportAndDelivery from 'components/client-directory/client-settings/transport-and-delivery/transport-and-delivery.component';
import PtjSettings from 'components/client-directory/client-settings/ptj-settings/ptj-settings.component';
import RelatedClinics from 'components/client-directory/client-settings/related-clinics/related-clinics.component';
import Invoices from 'components/client-directory/client-settings/invoices/invoices.component';
import { labInvoiceSettingsTemplate } from 'apollo/templates/organization/query.graphql';

const ClientSettings: FC = () => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'organization', 'orders']);
    const currentUser = currentUserVar();
    const { tab, id } = useParams<{ tab: string; id: string }>();
    const [tabState, setTabState] = useState('client-details');
    const navigate = useNavigate();
    const [isPtjOrg, setIsPtjOrg] = useState(false);
    const currentOrg = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const { data: { labClient } = {}, refetch } = useQuery(labClientTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            clientId: id,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { currentLab } = {} } = useQuery(labInvoiceSettingsTemplate, {
        fetchPolicy: 'network-only',
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        const isPtjOrg = customerSettings.getCustomerSetting(
            currentOrg?.customerSettings,
            CONSTANTS.SETTINGS_NAMES.PTJ_ORGANIZATION,
        );
        setIsPtjOrg(isPtjOrg);
        setTabState(tab || 'dentists');
    }, [tab]);

    const handleTabChange = (_: unknown, newTabState: string) => {
        const routeMap: { [key: string]: string } = {
            'client-details': 'client-details#contactDetails',
            'price-list': 'price-list#action-list',
            clinics: 'clinics',
            dentists: 'dentists',
            invoices: 'invoices#content',
            'transport-and-delivery': 'transport-and-delivery',
        };
        if (isPtjOrg) {
            routeMap['ptj-settings'] = 'ptj-settings';
        }

        if (tabState !== newTabState) {
            const url = `/client-directory/clients/${id}/${routeMap[newTabState]}`;
            navigate(url);
            setTabState(newTabState);
        }
    };

    const handleSettingsUpdated = () => {
        refetch();
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('common:CHANGES_SAVED'),
        });
    };

    return (
        <Fragment>
            {permissions.isLabTechnician(currentUser.type) ? (
                <Fragment>
                    {labClient ? (
                        <Paper variant="outlined" style={{ overflowX: 'auto' }}>
                            <ClientSettingsHeader labClient={labClient} />
                            <TabContext value={tabState}>
                                <Box display="flex" mb={2} ml={2}>
                                    <Box display="flex">
                                        <TabList
                                            sx={{ minWidth: 250 }}
                                            onChange={handleTabChange}
                                            indicatorColor="primary"
                                            textColor="primary"
                                            orientation="vertical"
                                        >
                                            <SettingsTab
                                                label={translate('client-directory:CLIENT_DETAILS')}
                                                value="client-details"
                                            />
                                            {labClient.customer?.type === 'clinic' && (
                                                <SettingsTab label={translate('common:DENTISTS')} value="dentists" />
                                            )}
                                            <SettingsTab
                                                label={translate('client-directory:INVOICING')}
                                                value="invoices"
                                            />
                                            <SettingsTab
                                                label={translate('client-directory:PRICE_LISTS')}
                                                value="price-list"
                                            />
                                            <SettingsTab
                                                label={translate('client-directory:TRANSPORT_AND_DELIVERY')}
                                                value="transport-and-delivery"
                                            />
                                            {isPtjOrg ? (
                                                <SettingsTab
                                                    label={translate('client-directory:PTJ_SETTINGS')}
                                                    value="ptj-settings"
                                                />
                                            ) : undefined}
                                            {(labClient?.customer?.isMain || labClient?.customer?.parentClinicId) &&
                                            labClient?.customer?.customerType === CustomerType.DENTHUB_CONNECTED ? (
                                                <SettingsTab label={translate('orders:ALL_CLINICS')} value="clinics" />
                                            ) : undefined}
                                        </TabList>
                                        <Box mx={3}>
                                            <Divider orientation="vertical" />
                                        </Box>
                                    </Box>
                                    <Box flexGrow={1} pr={2}>
                                        <TabPanel sx={{ padding: 0 }} value="client-details">
                                            <ClientDetails
                                                labClient={labClient}
                                                onClientDetailsSaved={handleSettingsUpdated}
                                            />
                                        </TabPanel>
                                        {labClient.customer?.type === 'clinic' && (
                                            <TabPanel sx={{ padding: 0 }} value="dentists">
                                                <ClientDirectoryDentists />
                                            </TabPanel>
                                        )}
                                        <TabPanel sx={{ padding: 0 }} value="invoices">
                                            {currentLab ? (
                                                <Invoices
                                                    labClient={labClient}
                                                    onInvoiceSettingsSaved={handleSettingsUpdated}
                                                    currentLab={currentLab}
                                                />
                                            ) : undefined}
                                        </TabPanel>
                                        <TabPanel sx={{ padding: 0 }} value="price-list">
                                            <PriceList labClient={labClient} />
                                        </TabPanel>
                                        <TabPanel sx={{ padding: 0 }} value="transport-and-delivery">
                                            <TransportAndDelivery />
                                        </TabPanel>
                                        {isPtjOrg ? (
                                            <TabPanel sx={{ padding: 0 }} value="ptj-settings">
                                                <PtjSettings />
                                            </TabPanel>
                                        ) : undefined}
                                        {(labClient?.customer?.isMain || labClient?.customer?.parentClinicId) &&
                                        labClient?.customer?.customerType === CustomerType.DENTHUB_CONNECTED ? (
                                            <TabPanel sx={{ padding: 0 }} value="clinics">
                                                <RelatedClinics />
                                            </TabPanel>
                                        ) : undefined}
                                    </Box>
                                </Box>
                            </TabContext>
                        </Paper>
                    ) : undefined}
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

interface SettingsTabProps {
    label: string;
    value: string;
    theme?: Theme;
}
const SettingsTab = styled((props: SettingsTabProps) => <Tab {...props} />)(({ theme }: { theme?: Theme }) => {
    return {
        fontSize: '0.7em',
        maxWidth: 'none',
        '&.Mui-selected': {
            fontWeight: theme?.typography.fontWeightBold,
            background: theme?.palette.primary.light,
            color: '#fff',
        },
        '&:hover': {
            opacity: 0.8,
        },
        alignItems: 'start',
        textAlign: 'left',
    };
});
export default ClientSettings;
